// Mosaic panel layout + CSV export matching the format Telescopius produces
// for ASIAIR import: "Pane, RA, DEC, Position Angle (East), Pane width
// (arcmins), Pane height (arcmins), Overlap, Row, Column", RA/Dec as HMS/DMS
// strings, one "Center" row for the whole mosaic plus one row per panel.

const pad2 = (n) => String(Math.trunc(n)).padStart(2, '0');

// Rotates a local (dx, dy) offset by rotationDeg, in the same flat
// tangent-plane approximation used by both the mosaic panel grid and the
// single-sensor framing box (TargetFrame.js), so they always agree.
export const rotateOffset = (dx, dy, rotationDeg) => {
    const theta = (rotationDeg * Math.PI) / 180;
    return [
        dx * Math.cos(theta) - dy * Math.sin(theta),
        dx * Math.sin(theta) + dy * Math.cos(theta)
    ];
};

export const raToHms = (raDeg) => {
    const hoursTotal = ((raDeg % 360) + 360) % 360 / 15;
    const h = Math.trunc(hoursTotal);
    const mFloat = (hoursTotal - h) * 60;
    const m = Math.trunc(mFloat);
    const s = Math.round((mFloat - m) * 60);
    return `${pad2(h)}hr ${pad2(m)}' ${pad2(s)}"`;
};

export const decToDms = (decDeg) => {
    const sign = decDeg < 0 ? '-' : '';
    const abs = Math.abs(decDeg);
    const d = Math.trunc(abs);
    const mFloat = (abs - d) * 60;
    const m = Math.trunc(mFloat);
    const s = Math.round((mFloat - m) * 60);
    return `${sign}${pad2(d)}º ${pad2(m)}' ${pad2(s)}"`;
};

// Panel centers for an R x C grid around (raCenter, decCenter), spaced by the
// sensor FOV minus the overlap fraction, in the same rotated flat-tangent
// approximation the framing box already uses. One shared rotation is applied
// to every panel (Telescopius/real astrometry gives each panel a hair-thin
// different position angle; that precision isn't meaningful here).
export const mosaicPanels = ({ raCenter, decCenter, fovWidthDeg, fovHeightDeg, rotationDeg, rows, cols, overlapFrac }) => {
    const cosDec = Math.cos((decCenter * Math.PI) / 180) || 1;
    const stepX = fovWidthDeg * (1 - overlapFrac);
    const stepY = fovHeightDeg * (1 - overlapFrac);
    const panels = [];
    let n = 0;
    for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
            n += 1;
            const localX = (c - (cols - 1) / 2) * stepX;
            const localY = ((rows - 1) / 2 - r) * stepY;
            const [rx, ry] = rotateOffset(localX, localY, rotationDeg);
            panels.push({
                label: `Pane ${n}`,
                ra: raCenter + rx / cosDec,
                dec: decCenter + ry,
                row: r + 1,
                col: c + 1
            });
        }
    }
    return panels;
};

export const mosaicCsv = ({ raCenter, decCenter, fovWidthDeg, fovHeightDeg, rotationDeg, rows, cols, overlapPct }) => {
    const overlapFrac = overlapPct / 100;
    const widthArcmin = (fovWidthDeg * 60).toFixed(2);
    const heightArcmin = (fovHeightDeg * 60).toFixed(2);
    const overlapLabel = `${overlapPct}%`;

    const lines = ['Pane, RA, DEC, Position Angle (East), Pane width (arcmins), Pane height (arcmins), Overlap, Row, Column'];
    lines.push([
        'Center', raToHms(raCenter), decToDms(decCenter), rotationDeg.toFixed(2),
        widthArcmin, heightArcmin, overlapLabel, '-', '-'
    ].join(', '));

    const panels = mosaicPanels({ raCenter, decCenter, fovWidthDeg, fovHeightDeg, rotationDeg, rows, cols, overlapFrac });
    panels.forEach((p) => {
        lines.push([
            p.label, raToHms(p.ra), decToDms(p.dec), rotationDeg.toFixed(2),
            widthArcmin, heightArcmin, overlapLabel, p.row, p.col
        ].join(', '));
    });

    return { csv: lines.join('\n'), panels };
};
